import { useBulkCreate } from "../../../hook/useBulkCreate";
import { getMetaData, getZendeskUrl, getCurrentUserId } from "../../../service/zendesk";
import { useFormatResponses } from "../../../hook/useFormatResponses";
import { ContentCreateProps } from "./contentCreateType";
import { useNotification } from "../../../components/notification";
import { useState } from "react";

export function useContentCreate({ type, headers, rows, result, setResult, loading, setLoading }: ContentCreateProps) {
    const hasData = headers.length > 0;
    const { massiveCreate } = useBulkCreate({ loading, setLoading })
    const { buildOrganizationsPayload, buildUsersPayload, buildTicketsPayload } = useFormatResponses()
    const { showNotification } = useNotification()
    const status = ["new", "open", "pending", "awaiting", "solved"] as const;
    const [commentType, setCommentType] = useState<"public" | "internal">("public");

    type StatusType = typeof status[number];

    const isStatus = (value: unknown): value is StatusType =>
        typeof value === "string" && status.includes(value as StatusType);

    const handleCreate = async () => {
        // if (type !== "user") return;

        const metadata = await getMetaData();
        const email = metadata.emailAdmin;
        const token = metadata.apiToken;
        const subdomain = getZendeskUrl();

        if (type === "ticket") {
            try {
                const currentUserId = await getCurrentUserId();
                const payload = {
                    ...(await buildTicketsPayload({ headers, rows, currentUserId, commentType })),
                    subdomain,
                    email,
                    token
                };

                const results = await massiveCreate({ type, payload });
                setResult(results);
                return results;
            } catch (error: any) {
                console.error("Erro ao criar tickets:", error);
                const erro = error.response?.data?.validation_errors[0].error;
                showNotification({
                    type: "error",
                    title: "Erro na criação dos tickets",
                    message: erro || "Ocorreu um erro ao criar os tickets. Por favor, revise os dados do arquivo e tente novamente.",
                });
                throw error;
            }
        }

        if (type === "user") {
            try {
                const payload = {
                    ...buildUsersPayload({ headers, rows }),
                    subdomain,
                    email,
                    token
                };
                const results = await massiveCreate({ type, payload });
                setResult(results)
                return results;
            } catch (error: any) {
                console.error("Erro ao criar usuários:", error);
                showNotification({
                    type: "error",
                    title: "Erro na criação dos usuários",
                    message: `${error.response?.data?.duplicate_errors.map((err: any) => `• O usuário enviado ${err.name}: ${err.message} (CPF: ${err.cpf})`).join("\n")} ` || "Ocorreu um erro ao criar os usuários. Por favor, revise os dados do arquivo e tente novamente.",
                });
                throw error;
            }
        }

        if (type === "organization") {
            try {
                const payload = {
                    ...buildOrganizationsPayload({ headers, rows }),
                    subdomain,
                    email,
                    token
                };
                const results = await massiveCreate({ type, payload });
                setResult(results)
                return results;
            } catch (error: any) {
                console.error("Erro ao criar organizações:", error);
                showNotification({
                    type: "error",
                    title: "Erro na criação das organizações",
                    message: "Ocorreu um erro ao criar as organizações. Por favor, revise os dados do arquivo e tente novamente.",
                });
                throw error;
            }
        }

        console.warn("Tipo não suportado:", type);
    };

    const handleReset = () => {
        setResult(null);
    };

    function countErrors(results: any[], type: string): { count: number; errors: any[] } {
        const errors: any[] = [];
        
        for (const jobResult of results) {
            if (jobResult.results && Array.isArray(jobResult.results)) {
                for (const item of jobResult.results) {
                    const success = type === "user" ? item.status === "Created" : !!item.id;
                    if (!success) {
                        errors.push(item);
                    }
                }
            }
        }
        
        return { count: errors.length, errors };
    }

    return {
        hasData,
        loading,
        handleCreate,
        result,
        handleReset,
        status,
        isStatus,
        setCommentType,
        commentType,
        countErrors
    }
}